import React, { useState } from "react";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import { DataGrid } from "@mui/x-data-grid";
import {
  COLUMN_USER,
  ROLE_USER,
  TOKEN_USERNAME_APP,
} from "../utils/constDispatch";
import { API_DelUser, API_EditUser, API_ResetPassword } from "../utils/http";
import { generatePassword } from "../utils/password";
import { PasswordBox } from "./PasswordBox";
import { Toast } from "./Toast";

const btnSx = {
  borderRadius: "20px",
  textTransform: "none",
  fontWeight: 500,
  fontSize: 13,
};

const dialogTitleSx = {
  fontWeight: 600,
  fontSize: 17,
  borderBottom: "1px solid #E0E0E0",
  backgroundColor: "#F5F7FA",
  py: 1.5,
  px: 3,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: 13,
  },
  "& .MuiInputLabel-root": {
    fontSize: 13,
  },
};

// `role` on a row is already the display label — User.js overwrites it with
// showRole() before handing rows over — so map back to the numeric value.
const ROLE_OPTIONS = [
  { value: ROLE_USER.ADMIN, label: "ADMIN" },
  { value: ROLE_USER.OPERATOR, label: "OPERATOR" },
  { value: ROLE_USER.EDITER, label: "USER" },
];

const roleValueOf = (label) =>
  ROLE_OPTIONS.find((x) => x.label === label)?.value ?? ROLE_USER.EDITER;

export default function ListUser({ user_arr, get_user }) {
  const [rowSelection, setRowSelection] = useState([]);
  const [row_click, set_row_click] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [pdf_created_by, setPdfCreatedBy] = useState("");
  const [role, set_role] = useState(ROLE_USER.EDITER);

  const [confirm_delete, set_confirm_delete] = useState(false);
  const [confirm_reset, set_confirm_reset] = useState(false);
  // The generated password stays on screen after the reset succeeds — the admin
  // still has to pass it on, and there is no way to look it up again later.
  const [reset_password, set_reset_password] = useState("");
  const [reset_done, set_reset_done] = useState(false);
  const [error_msg, set_error_msg] = useState("");
  const [toast, set_toast] = useState(null);

  const is_self =
    row_click?.username === sessionStorage.getItem(TOKEN_USERNAME_APP);

  // Clearing the selection matters: without it, re-clicking the same row is not
  // a selection *change* and would never reopen the dialog.
  const CloseModal = () => {
    setOpenModal(false);
    setRowSelection([]);
    set_row_click(null);
    set_error_msg("");
  };

  const OpenRow = (id) => {
    const item = user_arr.find((x) => x._id === id);
    if (item == null) return;
    set_row_click(item);
    set_role(roleValueOf(item.role));
    setPdfCreatedBy(item.pdf_created_by ?? "");
    set_error_msg("");
    setOpenModal(true);
  };

  const SaveUser = () => {
    set_error_msg("");
    let body = {
      data: {
        role: role,
        pdf_created_by: pdf_created_by,
      },
      _id: row_click._id,
    };

    API_EditUser(body, (data) => {
      if (data?.success) {
        set_toast({ severity: "success", text: `Đã lưu user ${row_click.username}` });
        CloseModal();
        get_user();
      } else {
        set_error_msg(data?.message || "Lưu user thất bại");
      }
    });
  };

  const OpenReset = () => {
    set_error_msg("");
    set_reset_done(false);
    set_reset_password(generatePassword());
    set_confirm_reset(true);
  };

  const CloseReset = () => {
    set_confirm_reset(false);
    set_reset_done(false);
    set_reset_password("");
    set_error_msg("");
  };

  const ResetUser = (username) => {
    set_error_msg("");
    API_ResetPassword(username, reset_password, (data) => {
      if (data?.success) {
        // Keep the dialog open on success so the password stays readable.
        set_reset_done(true);
        get_user();
      } else {
        set_error_msg(data?.message || "Reset password thất bại");
      }
    });
  };

  const DeleteUser = (username) => {
    set_error_msg("");
    API_DelUser(username, (data) => {
      if (data?.success) {
        set_confirm_delete(false);
        set_toast({ severity: "success", text: `Đã xoá user ${username}` });
        CloseModal();
        get_user();
      } else {
        // Keep the dialog open so the failure is visible instead of silent.
        set_error_msg(data?.message || "Xoá user thất bại");
      }
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Dialog
        open={openModal}
        onClose={() => CloseModal()}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: "12px" },
        }}
      >
        <DialogTitle sx={dialogTitleSx}>Edit User</DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 12, color: "#888" }}>
                Username
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#333" }}>
                {row_click?.username}
                {is_self && (
                  <Typography
                    component="span"
                    sx={{ fontSize: 12, color: "#888", fontWeight: 400, ml: 1 }}
                  >
                    (bạn)
                  </Typography>
                )}
              </Typography>
            </Box>

            <TextField
              select
              size="small"
              label="Role"
              value={role}
              onChange={(e) => set_role(e.target.value)}
              sx={fieldSx}
            >
              {ROLE_OPTIONS.map((x) => (
                <MenuItem key={x.value} value={x.value} sx={{ fontSize: 13 }}>
                  {x.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Created By"
              value={pdf_created_by}
              onChange={(e) => setPdfCreatedBy(e.target.value)}
              sx={fieldSx}
            />

            <Divider />

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LockResetIcon />}
                sx={btnSx}
                onClick={() => OpenReset()}
              >
                Reset Password
              </Button>
              <Tooltip
                title={is_self ? "Không thể tự xoá chính mình" : ""}
                disableHoverListener={!is_self}
              >
                {/* span wrapper: MUI disabled buttons don't fire hover events */}
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    disabled={is_self}
                    startIcon={<DeleteOutlineIcon />}
                    sx={btnSx}
                    onClick={() => {
                      set_error_msg("");
                      set_confirm_delete(true);
                    }}
                  >
                    Delete
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {error_msg !== "" && (
              <Typography sx={{ fontSize: 13, color: "#D32F2F" }}>
                {error_msg}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" sx={btnSx} onClick={() => CloseModal()}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            sx={btnSx}
            onClick={() => SaveUser()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirm_reset}
        onClose={() => CloseReset()}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={dialogTitleSx}>Reset Password</DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: "#333" }}>
              {reset_done ? (
                <>
                  Đã reset password của <b>{row_click?.username}</b>. Copy và gửi
                  cho user — đóng dialog là không xem lại được nữa.
                </>
              ) : (
                <>
                  Reset password của <b>{row_click?.username}</b> thành password
                  random dưới đây?
                </>
              )}
            </Typography>

            <PasswordBox
              value={reset_password}
              onRegenerate={
                reset_done
                  ? undefined
                  : () => set_reset_password(generatePassword())
              }
            />

            {error_msg !== "" && (
              <Typography sx={{ fontSize: 13, color: "#D32F2F" }}>
                {error_msg}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {reset_done ? (
            <Button
              size="small"
              variant="contained"
              disableElevation
              sx={btnSx}
              onClick={() => CloseReset()}
            >
              Xong
            </Button>
          ) : (
            <>
              <Button size="small" sx={btnSx} onClick={() => CloseReset()}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={btnSx}
                onClick={() => ResetUser(row_click.username)}
              >
                Reset
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirm_delete}
        onClose={() => set_confirm_delete(false)}
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={dialogTitleSx}>Delete User</DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Typography sx={{ fontSize: 13, color: "#333" }}>
            Xoá user <b>{row_click?.username}</b>? Hành động này không thể hoàn
            tác.
          </Typography>
          {error_msg !== "" && (
            <Typography sx={{ fontSize: 13, color: "#D32F2F", mt: 1.5 }}>
              {error_msg}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            sx={btnSx}
            onClick={() => set_confirm_delete(false)}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disableElevation
            sx={btnSx}
            onClick={() => DeleteUser(row_click.username)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Toast value={toast} onClose={() => set_toast(null)} />

      {user_arr.length === 0 ? null : (
        <Box
          sx={{
            "& .MuiDataGrid-root": {
              border: "1px solid #E0E0E0",
              borderRadius: "10px",
              overflow: "hidden",
              fontSize: 13,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F5F7FA",
              fontSize: 13,
              fontWeight: 600,
              color: "#555",
              borderBottom: "1px solid #E0E0E0",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "#F8FBFF",
              },
              "&.Mui-selected": {
                backgroundColor: "#EBF4FF",
                "&:hover": {
                  backgroundColor: "#DDEEFF",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #F0F0F0",
              py: 1,
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #E0E0E0",
              backgroundColor: "#FAFAFA",
            },
          }}
        >
          <DataGrid
            getRowId={(item) => item._id}
            rows={user_arr}
            columns={COLUMN_USER}
            pageSizeOptions={[5, 10, 20]}
            rowSelectionModel={rowSelection}
            onRowSelectionModelChange={(e) => {
              setRowSelection(e);
              OpenRow(e[0]);
            }}
            disableMultipleRowSelection={true}
          />
        </Box>
      )}
    </Box>
  );
}
